const MIN_DURATION_MS = 2400;
const REDUCED_MIN_MS = 400;
const DISMISS_MS = 650;
const COMPLETE_BEAT_MS = 900;
const APPROACH_TAU_MS = 3200;
const HOLD_CAP = 0.9;

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Host do ritual: overlay fullscreen + crawl 3D (R3F) até a API responder.
 * O módulo 3D é carregado sob demanda no begin() (code-split).
 */
export class DungeonRitual {
  /**
   * @param {HTMLElement} mountParent
   */
  constructor(mountParent) {
    this.mountParent = mountParent;
    this.root = null;
    this._mountEl = null;
    this._api = null;
    this._raf = 0;
    this._minTimer = null;
    this._playing = false;
    this._reduced = false;
    this._startedAt = 0;
    this._minReached = false;
    this._apiDone = false;
    this._completing = false;
    this._completeResolve = null;
    this._finishStartedAt = 0;
    this._progressAtFinishStart = 0;
    this._progress = 0;
    this._loadToken = 0;
  }

  begin() {
    this._hardReset();
    this._reduced = prefersReducedMotion();
    this._playing = true;
    this._minReached = false;
    this._apiDone = false;
    this._completing = false;
    this._progress = 0;
    this._startedAt = performance.now();
    this._lockScroll(true);
    this._buildShell();

    const minMs = this._reduced ? REDUCED_MIN_MS : MIN_DURATION_MS;
    this._minTimer = window.setTimeout(() => {
      this._minReached = true;
      this._tryFinish();
    }, minMs);

    const token = ++this._loadToken;
    void import('../dungeon/mount.jsx')
      .then((mod) => {
        if (!this._playing || token !== this._loadToken) return;
        this._api = mod;
        mod.mountDungeonCrawl(this._mountEl, {
          progress: 0,
          finishing: false,
          reducedMotion: this._reduced,
        });
        this._raf = requestAnimationFrame(this._tick);
      })
      .catch((err) => {
        console.error('[dungeon] failed to load crawl', err);
      });
  }

  complete() {
    if (!this._playing && !this.root) return Promise.resolve();
    if (this._completeResolve) {
      return new Promise((resolve) => {
        const prev = this._completeResolve;
        this._completeResolve = () => {
          prev();
          resolve();
        };
      });
    }
    this._apiDone = true;
    return new Promise((resolve) => {
      this._completeResolve = resolve;
      this._tryFinish();
    });
  }

  dismiss() {
    this._teardown(true);
  }

  get playing() {
    return this._playing;
  }

  _tick = () => {
    if (!this._playing) return;
    const now = performance.now();

    if (this._completing) {
      const u = Math.min(1, (now - this._finishStartedAt) / COMPLETE_BEAT_MS);
      const eased = 1 - (1 - u) ** 3;
      this._progress =
        this._progressAtFinishStart + (1 - this._progressAtFinishStart) * eased;
      this._api?.updateDungeonCrawl({
        progress: this._progress,
        finishing: true,
        reducedMotion: this._reduced,
      });
      if (u >= 1) {
        this._finish();
        return;
      }
    } else {
      const elapsed = now - this._startedAt;
      const approach = 1 - Math.exp(-elapsed / APPROACH_TAU_MS);
      this._progress = Math.min(HOLD_CAP, approach * HOLD_CAP);
      this._api?.updateDungeonCrawl({
        progress: this._progress,
        finishing: false,
        reducedMotion: this._reduced,
      });
    }

    this._raf = requestAnimationFrame(this._tick);
  };

  _tryFinish() {
    if (!this._apiDone || !this._minReached || this._completing) return;
    this._completing = true;
    this._finishStartedAt = performance.now();
    this._progressAtFinishStart = this._progress;
    window.clearTimeout(this._minTimer);
    this._minTimer = null;
  }

  _finish() {
    this._teardown(false);
  }

  _teardown(immediateResolve) {
    this._loadToken += 1;
    window.clearTimeout(this._minTimer);
    this._minTimer = null;
    if (this._raf) {
      cancelAnimationFrame(this._raf);
      this._raf = 0;
    }
    this._playing = false;
    this._lockScroll(false);

    try {
      this._api?.unmountDungeonCrawl();
    } catch {
      /* ignore */
    }
    this._api = null;

    if (this.root) {
      this.root.classList.add('is-leaving');
      const el = this.root;
      const reduced = this._reduced;
      this.root = null;
      this._mountEl = null;
      window.setTimeout(() => el.remove(), immediateResolve || reduced ? 0 : DISMISS_MS);
    }

    if (this._completeResolve) {
      const resolve = this._completeResolve;
      this._completeResolve = null;
      if (immediateResolve || this._reduced) resolve();
      else window.setTimeout(resolve, Math.min(DISMISS_MS, 320));
    }
  }

  _hardReset() {
    this._loadToken += 1;
    window.clearTimeout(this._minTimer);
    this._minTimer = null;
    if (this._raf) {
      cancelAnimationFrame(this._raf);
      this._raf = 0;
    }
    if (this._completeResolve) {
      const prev = this._completeResolve;
      this._completeResolve = null;
      prev();
    }
    try {
      this._api?.unmountDungeonCrawl();
    } catch {
      /* ignore */
    }
    this._api = null;
    if (this.root) {
      this.root.remove();
      this.root = null;
    }
    this._mountEl = null;
    this._playing = false;
    this._lockScroll(false);
  }

  _buildShell() {
    const el = document.createElement('div');
    el.className = 'dungeon-ritual';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-busy', 'true');
    el.innerHTML = `<div class="dungeon-ritual__mount"></div>`;
    this.root = el;
    this._mountEl = el.querySelector('.dungeon-ritual__mount');
    this.mountParent.appendChild(el);
    requestAnimationFrame(() => el.classList.add('is-active'));
  }

  _lockScroll(locked) {
    if (typeof document === 'undefined') return;
    document.documentElement.style.overflow = locked ? 'hidden' : '';
    document.body.style.overflow = locked ? 'hidden' : '';
  }
}
